import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-fade";
import { api } from "@/lib/api";
import { Sparkles, ArrowRight, Clock } from "lucide-react";

function useNow(intervalMs = 60000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

function formatCountdown(endIso, now) {
  if (!endIso) return null;
  const end = new Date(endIso).getTime();
  const ms = end - now;
  if (ms <= 0) return null;
  const totalMin = Math.floor(ms / 60000);
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function isEndingSoon(endIso, now) {
  if (!endIso) return false;
  const ms = new Date(endIso).getTime() - now;
  return ms > 0 && ms < 24 * 60 * 60 * 1000; // <24h
}

export default function DealStrip() {
  const [deals, setDeals] = useState([]);
  const now = useNow(60000);

  useEffect(() => {
    api.get("/deals").then((r) => setDeals(r.data.deals || []));
  }, []);

  const visible = useMemo(
    () => deals.filter((d) => !d.end_at || new Date(d.end_at).getTime() > now),
    [deals, now]
  );

  if (!visible.length) return null;

  return (
    <div data-testid="deal-strip" className="relative bg-[#0B1120] border-b border-white/5">
      <Swiper
        modules={[Autoplay, EffectFade]}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        autoplay={{ delay: 4200, disableOnInteraction: false }}
        loop={true}
        className="max-w-[1400px] mx-auto"
      >
        {visible.map((d, i) => {
          const countdown = formatCountdown(d.end_at, now);
          const urgent = isEndingSoon(d.end_at, now);
          return (
            <SwiperSlide key={d.id || i}>
              <Link
                to={`/packages/${d.slug}`}
                data-testid={`deal-${d.slug}`}
                className="flex items-center gap-3 px-6 lg:px-10 py-3 text-sm"
              >
                <span
                  className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold shrink-0"
                  style={{ backgroundColor: d.color + "22", color: d.color }}
                >
                  <Sparkles className="w-3 h-3" /> {d.tag}
                </span>
                <span className="text-white font-semibold truncate">{d.title}</span>
                <span className="text-white/40 hidden md:inline">·</span>
                <span className="text-[#D4AF37] font-semibold truncate">{d.line}</span>
                {countdown && (
                  <span
                    data-testid={`deal-countdown-${d.slug}`}
                    className={`hidden md:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold shrink-0 ${
                      urgent
                        ? "bg-red-500/20 text-red-300 animate-pulse"
                        : "bg-white/10 text-white/80"
                    }`}
                  >
                    <Clock className="w-3 h-3" /> Ends in {countdown}
                  </span>
                )}
                <span className="flex-1" />
                <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-bold text-white whitespace-nowrap">
                  {d.cta} <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
}
